import { ParameterSectionParameterType, ParameterSectionType } from 'common/components/parametersSections/models';
import ParameterMinMax from '../parameterMinMax/ParameterMinMax';
import ParameterProducts from '../parameterProducts/ParameterProducts';
import ParameterSection from '../parameterSection/ParameterSection';
import styles from './Parameter.module.scss';
import { BudgetLineParameterDto } from 'api/budgets/models/BudgetLineParameterDto';
import ParameterImages from '../parameterImages/ParameterImages';
import ParameterNumber from '../parameterNumber/ParameterNumber';
import ParameterText from '../parameterText/ParameterText';
import ParameterQuantity from '../parameterQuantity/ParameterQuantity';
import ParameterSupplier from '../parameterSupplier/ParameterSupplier';
import ParameterDate from '../parameterDate/ParameterDate';
import ParameterPrice from '../parameterPrice/ParameterPrice';
import { BudgetLineDto } from 'api/budgets/models/BudgetDto';
import { useDispatch } from 'react-redux';
import { updateLineParameter } from 'store/budget/action';
import { useEffect, useRef } from 'react';

interface Props {
    parameter: BudgetLineParameterDto;
    level: number;
    line: BudgetLineDto;
    disabled: boolean;
    highlightParameterKeys?: string[];
}

function Parameter({ parameter, level, line, disabled, highlightParameterKeys }: Props): JSX.Element | null {
    const dispatch = useDispatch();
    const parameterRef = useRef<HTMLDivElement>(null);
    const isHighlighted = highlightParameterKeys?.includes(parameter.key!) ?? false;

    useEffect(() => {
        if (isHighlighted && parameterRef.current) {
            parameterRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [isHighlighted]);

    const onChangeParameter = (newParameter: Partial<BudgetLineParameterDto>, _calculateTotals: boolean) => {
        // updateLineParameter already handles full recalculation for formula-type params
        // via RECALCULATE_LINE_PARAMETER (expressions + totals + budget totals)
        dispatch(updateLineParameter(line.key, {
            key: parameter.key,
            ...newParameter
        }));
    }

    if (parameter.type === ParameterSectionType.PARAMETER) {
        let content: JSX.Element | null;

        switch (parameter.parameterType) {
            case ParameterSectionParameterType.NUMBER_MIN_MAX:
                content = <ParameterMinMax parameter={parameter} line={line} disabled={disabled} onChange={onChangeParameter} />
                break;

            case ParameterSectionParameterType.PRODUCTS:
                content = <ParameterProducts parameter={parameter} line={line} disabled={disabled} onChange={onChangeParameter} />
                break;

            case ParameterSectionParameterType.IMAGES:
                content = <ParameterImages parameter={parameter} disabled={disabled} />
                break;

            case ParameterSectionParameterType.TEXT:
                content = <ParameterText parameter={parameter} disabled={disabled} onChange={onChangeParameter} />
                break;

            case ParameterSectionParameterType.NUMBER:
                content = <ParameterNumber parameter={parameter} line={line} disabled={disabled} onChange={onChangeParameter} />
                break;

            case ParameterSectionParameterType.QUANTITY:
                content = <ParameterQuantity parameter={parameter} disabled={disabled} line={line} onChange={onChangeParameter} />
                break;

            case ParameterSectionParameterType.SUPPLIER:
                content = <ParameterSupplier parameter={parameter} disabled={disabled} onChange={onChangeParameter} />
                break;

            case ParameterSectionParameterType.DATE:
                content = <ParameterDate parameter={parameter} disabled={disabled} onChange={onChangeParameter} />
                break;

            case ParameterSectionParameterType.PRICE:
                content = <ParameterPrice parameter={parameter} line={line} disabled={disabled} onChange={onChangeParameter} />
                break;

            case ParameterSectionParameterType.WEIGHT:
                content = <ParameterNumber parameter={parameter} line={line} disabled={disabled} onChange={onChangeParameter} />
                break;

            default:
                content = null;
        }

        if (!content && !parameter.parameterName) {
            return null;
        }

        return (
            <div ref={parameterRef} className={`${styles.parameter} ${isHighlighted ? styles.highlight : ''}`}>
                <div className={styles.parameterName}>{parameter.parameterName ?? '\u00A0'}</div>
                {content}
            </div>
        );
    }

    return <ParameterSection parameter={parameter} level={level} line={line} disabled={disabled} highlightParameterKeys={highlightParameterKeys} />
}

export default Parameter;
